import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { Box } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { GroupParticipationPeriodEntry } from '../../../../interfaces/insights.interface';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../../../i18n/useAppLocale';
import { getMonthName } from '../../../../i18n/analytics';

interface SparklineChartProps {
  data: GroupParticipationPeriodEntry[];
  width?: number | string;
  height?: number;
  color?: string;
}

const SparklineChart: React.FC<SparklineChartProps> = ({
  data,
  width = 270,
  height = 110,
  color = '#1976d2'
}) => {
  const { t } = useTranslation('insights');
  const appLocale = useAppLocale();
  const localizeMonth = (value: string) => {
    const month = ['janeiro','fevereiro','marco','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'].indexOf(value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()) + 1;
    return month > 0 ? getMonthName(month, appLocale, true) : value;
  };
  const series = useMemo(() => ([
    {
      data: data.map((item) => ({
        x: item.month,
        y: item.value
      }))
    }
  ]), [data]);

  const options: ApexOptions = useMemo(() => ({
     chart: {
      type: 'area',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      },
      selection: {
        enabled: false
      },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      parentHeightOffset: 0,
      animations: {
        enabled: false
      }
    },

    colors: [color],

    stroke: {
      curve: 'smooth',
      width: 2
    },

    markers: {
      size: 0,
      hover: {
        size: 5
      }
    },

    fill: {
      opacity: 0.15,
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0.05,
        stops: [0, 100]
      }
    },

    dataLabels: {
      enabled: false
    },

    annotations: {
      yaxis: [
        {
          y: 70,
          borderColor: '#52960e',
          borderWidth: 2,
          label: {
            borderColor: '#8fff1d',
            style: {
              color: '#3C4043',
              background: '#8fff1d',
              fontSize: '9px'
            },
            text: String(t('shared.good'))
          }
        }
      ]
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: false,
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const seriesData = w.config?.series?.[seriesIndex]?.data || [];
        const point = seriesData[dataPointIndex];
        const month = localizeMonth(point?.x ?? '');
        const value = point?.y ?? series?.[seriesIndex]?.[dataPointIndex] ?? null;

        const formattedValue = typeof value === 'number' && !Number.isNaN(value)
          ? `${value.toLocaleString(appLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
          : '-';

        return `
          <div style="
            padding:8px 12px;
            border-radius:8px;
            background:#fff;
            color:#0f1724;
            border:1px solid rgba(0,0,0,0.08);
            font-size:13px;
            box-shadow:0 6px 18px rgba(15,23,36,0.12)
          ">
            ${month} - ${formattedValue}
          </div>
        `;
      }
    },
   xaxis: {
      type: 'category',
      categories: data.map((d) => d.month),
      labels: {
        show: false
      },
      axisTicks: {
        show: false
      },
      axisBorder: {
        show: true,
        color: '#c4c4c4'
      },
      crosshairs: {
        width: 0
      }
    },

    grid: {
      show: false,
      padding: {
        left: 8,
        right: 8,
        top: 4,
        bottom: 0
      }
    },

    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 1,
      labels: {
        show: true,
        offsetX: -6,
        formatter: (value) => `${value}%`,
        style: {
          fontSize: '11px'
        }
      },
      axisBorder: {
        show: true,
        color: '#c4c4c4',
        
      },
      axisTicks: {
        show: false
      }
    }
  }), [color, data, appLocale, t]);

  return (
    <Box
      sx={{
        width: '100%',
        height: `${height}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible'
      }}
    >
      <Chart
        options={options}
        series={series}
        type="area"
        width={width}
        height={height}
      />
    </Box>
  );
};

export default SparklineChart;
